import React from 'react';
import { Button, Collapse } from 'antd';
import { CheckboxFilterList, CheckboxFilterListOption } from '../../../../shared/components/CheckboxFilterList';
import { DateFilterInputs } from '../../../../shared/components/DateFilterInputs';
import './FilterPanel.scss';

export interface FilterSelection {
  creators: string[];
  /** [from, to] ISO date strings; unset ends are "" */
  dateRange: [string, string];
  fileTypes: string[];
  maps: string[];
}

export const EMPTY_FILTER_SELECTION: FilterSelection = { creators: [], dateRange: ['', ''], fileTypes: [], maps: [] };

// Creator is intentionally ignored until the CREATOR panel is enabled.
export const isSelectionActive = (selection: FilterSelection) =>
  selection.fileTypes.length > 0 || selection.maps.length > 0 || selection.dateRange[0] !== '' || selection.dateRange[1] !== '';

export interface FilterPanelProps {
  selection: FilterSelection;
  onChange: (partial: Partial<FilterSelection>) => void;
  onApply: () => void;
  onClearAll: () => void;
  isApplying?: boolean;
  // Temporary mock data until Creator is enabled
  creatorOptions?: CheckboxFilterListOption[];
  mapOptions?: CheckboxFilterListOption[];
}

const { Panel } = Collapse;

const FILE_TYPE_OPTIONS: CheckboxFilterListOption[] = [
  { label: 'PDF', value: 'pdf' },
  { label: 'PNG', value: 'png' },
  { label: 'SHP', value: 'shp' },
  { label: 'KML', value: 'kml' },
];

export const FilterPanel: React.FC<FilterPanelProps> = ({
  selection,
  onChange,
  onApply,
  onClearAll,
  isApplying = false,
  creatorOptions = [],
  mapOptions = [],
}) => {
  const handleFromChange = (from: string) => {
    onChange({ dateRange: [from, selection.dateRange[1]] });
  };

  const handleToChange = (to: string) => {
    onChange({ dateRange: [selection.dateRange[0], to] });
  };

  return (
    <div className="filter-panel">
      <Collapse ghost className="filter-panel__accordion" defaultActiveKey={[]}>
        <Panel header="CREATOR" key="creator" showArrow={false} collapsible="disabled">
          <CheckboxFilterList options={creatorOptions} selectedValues={selection.creators} onChange={(creators) => onChange({ creators })} />
        </Panel>

        <Panel header="DATE" key="date" showArrow={false}>
          <DateFilterInputs
            fromDate={selection.dateRange[0]}
            toDate={selection.dateRange[1]}
            onFromChange={handleFromChange}
            onToChange={handleToChange}
          />
        </Panel>

        <Panel header="FILE TYPE" key="fileType" showArrow={false}>
          <CheckboxFilterList options={FILE_TYPE_OPTIONS} selectedValues={selection.fileTypes} onChange={(fileTypes) => onChange({ fileTypes })} />
        </Panel>

        <Panel header="MAP" key="map" showArrow={false}>
          <CheckboxFilterList options={mapOptions} selectedValues={selection.maps} onChange={(maps) => onChange({ maps })} />
        </Panel>
      </Collapse>

      <div className="filter-panel__divider" />

      <div className="filter-panel__footer">
        <Button type="text" className="filter-panel__action" onClick={onClearAll}>
          CLEAR ALL
        </Button>
        <Button type="text" className="filter-panel__action" onClick={onApply} disabled={isApplying}>
          APPLY
        </Button>
      </div>
    </div>
  );
};
